/**
 * The POI sheet in its pinned form — embedded on the 3D peak page rather than
 * opened over a map, the way the apps embed their POI sheet in the flyover.
 * Pure, so the rules are testable without mounting the sheet.
 */

/** How far a drag on the handle has to travel to be a swipe rather than a tap. */
export const SWIPE_THRESHOLD_PX = 30;

/** Below this width the sheet is a bottom sheet; from it, a right-hand panel. */
export const DESKTOP_MIN_WIDTH_PX = 992;

/**
 * Whether the pinned sheet ends up open after a gesture on its handle. `dy` is
 * how far the pointer moved down (negative when it moved up). A swipe up opens
 * it and a swipe down settles it back to its peek; anything shorter is a tap,
 * which flips it. It never goes away altogether — there is no closed state.
 */
export function nextPinnedExpanded(expanded: boolean, dy: number): boolean {
  if (dy <= -SWIPE_THRESHOLD_PX) return true;
  if (dy >= SWIPE_THRESHOLD_PX) return false;
  return !expanded;
}

/**
 * The height to show for the POI. GetPOIByID carries none, so the page that
 * pins the sheet hands over what it knows (the peak's height, or the DEM's);
 * a zero or negative reading is "unknown", not sea level.
 */
export function pinnedElevation(
  poiElevation: number | null | undefined,
  fallback: number | null | undefined,
): number | null {
  if (typeof poiElevation === 'number' && !Number.isNaN(poiElevation)) return poiElevation;
  if (typeof fallback === 'number' && fallback > 0) return fallback;
  return null;
}

/**
 * How much of the bottom of the screen the sheet covers, for the page's own
 * controls to sit above. Nothing on a wide screen, where the sheet is a panel
 * down the right rather than across the bottom.
 */
export function sheetBottomOffset(windowWidth: number, windowHeight: number, sheetTop: number): number {
  if (windowWidth >= DESKTOP_MIN_WIDTH_PX) return 0;
  return Math.max(Math.round(windowHeight - sheetTop), 0);
}
